/**
 * useCountUp — fait grimper un nombre de 0 (ou de sa valeur précédente) vers
 * `cible` en rAF, easeOutCubic. Copie locale du hook de la fête de la vente
 * (`panels/regie/celebration/useCountUp.ts`) : la librairie partagée ne doit
 * dépendre d'aucun panneau, et ce hook doit se porter tel quel sur Helios.
 *
 * `prefers-reduced-motion` → saut immédiat à `cible`.
 */
import { useEffect, useRef, useState } from "react";

export function prefereMoinsDeMouvement(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

export interface CountUpOptions {
  dureeMs?: number;
  /** Départ imposé (défaut : la valeur précédente, ou `cible` au 1ᵉʳ rendu). */
  depuis?: number;
  /** Retard avant de partir (ms) — pour caler le compteur sur l'arrivée du bloc. */
  retardMs?: number;
}

export function useCountUp(cible: number, opts: CountUpOptions = {}): number {
  const { dureeMs = 900, depuis, retardMs = 0 } = opts;
  const [valeur, setValeur] = useState(depuis ?? cible);
  const rafRef = useRef<number | null>(null);
  const depuisRef = useRef(depuis ?? cible);

  useEffect(() => {
    if (prefereMoinsDeMouvement() || dureeMs <= 0) {
      setValeur(cible);
      return;
    }
    const depart = depuisRef.current;
    if (depart === cible) {
      setValeur(cible);
      return;
    }
    let debut: number | null = null;
    const tick = (ts: number) => {
      if (debut === null) debut = ts;
      const p = Math.min(1, (ts - debut) / dureeMs);
      setValeur(depart + (cible - depart) * easeOutCubic(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else depuisRef.current = cible;
    };
    const timer = window.setTimeout(() => {
      rafRef.current = requestAnimationFrame(tick);
    }, retardMs);
    return () => {
      window.clearTimeout(timer);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      depuisRef.current = cible;
    };
  }, [cible, dureeMs, retardMs]);

  return valeur;
}
